import axios, { Axios, AxiosResponse, AxiosError } from "axios";
import { config } from "./Config";
import PagedResponse from "../types/PagedResponse";
import { getAccessToken } from "./TokenRefresh";

export default class HttpClient<T> {
  private _baseUrl: string = '';
  private _endpoint: string = '';
  private _apiClient: Axios;

  constructor(endpoint: string) {
    this._baseUrl = config.apiUrl;
    this._endpoint = endpoint;
    this._apiClient = axios.create({
      baseURL: this._baseUrl,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this._apiClient.interceptors.request.use(
      async (reqConfig) => {
        const token = await getAccessToken();
        if (token) reqConfig.headers.Authorization = `Bearer ${token}`;
        return reqConfig;
      },
      (error) => Promise.reject(error)
    );

    this._apiClient.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        const originalRequest = error.config as any;
        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;
          const newToken = await getAccessToken(true);
          if (newToken) {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return this._apiClient.request(originalRequest);
          }
        }
        return Promise.reject(error);
      }
    );
  }

  public async getAllPaged(queryParams?: Record<string, string>): Promise<PagedResponse<T>> {
    const queryString = queryParams ? `?${new URLSearchParams(queryParams).toString()}` : '';
    const response = await this._apiClient.get(`${this._endpoint}${queryString}`);
    return response.data;
  }

  public async getAll(queryParams?: Record<string, string>): Promise<T[]> {
    const queryString = queryParams ? `?${new URLSearchParams(queryParams).toString()}` : '';
    const response = await this._apiClient.get(`${this._endpoint}${queryString}`);
    return response.data ?? [];
  }

  public async getById(id: number) : Promise<{ status: number; data: T }> {
    const response = await this._apiClient.get(`${this._endpoint}/${id}`);
    return response.data;
  }

  public async post(model: T) : Promise<AxiosResponse<T>> {
    const response = await this._apiClient.post(`${this._endpoint}`, model);
    return response.data;
  }

  public async postAction(actionName: string, body?: unknown) : Promise<AxiosResponse<T>> {
    const response = await this._apiClient.post(`${this._endpoint}/${actionName}`, body ?? {});
    return response.data;
  }

  public async put(model: T) : Promise<AxiosResponse<T>> {
    const response = await this._apiClient.put(`${this._endpoint}`, model);
    return response;
  }

  public async getSingle(queryParams?: Record<string, string>): Promise<T | null> {
    const queryString = queryParams ? `?${new URLSearchParams(queryParams).toString()}` : '';
    const response = await this._apiClient.get(`${this._endpoint}${queryString}`);
    return response.data ?? null;
  }

  public async patch(id: number, data: Partial<T>): Promise<AxiosResponse> {
    const response = await this._apiClient.patch(`${this._endpoint}/${id}`, data);
    return response.data;
  }

  public async patchAction(actionName: string, data?: unknown): Promise<AxiosResponse> {
    const response = await this._apiClient.patch(`${this._endpoint}/${actionName}`, data ?? {});
    return response.data;
  }

  public async delete(id: number): Promise<AxiosResponse<T, any>> {
      const response = await this._apiClient.delete(`${this._endpoint}/${id}`);
      return response;
  }
}
